import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQueries } from "@tanstack/react-query";
import { Button } from "@/Button";
import { AnimatePresence, motion } from "framer-motion";
import { PetDataType, TicketListItem } from "~/types/common";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { useTicketList } from "~/services/customer/myPage";
import { useCalendarList } from "~/services/customer/calendar";
import {
  useReserveForms,
  useCreateReserve,
  useReservableDate,
  useReservableTime,
  getReservableTime,
  useReservationPetList,
  checkReservationCapacity,
} from "~/services/reservation";
import { Dialog } from "@/Dialog";
import ContractModal from "@/common/ContractModal";
import { useEnsureContractRequest } from "~/services/customer/contractRequest";
import { getErrorMessage } from "%/errorCode";
import useHideNav from "^/common/useHideNav";

interface ReservationWithSubscriptionParams {
  companySeq?: string;
  cdProductType?: string;
  restoreState?: string; // 복원할 선택 상태 (JSON 문자열)
  fromPayment?: string; // "1"이면 결제 완료 화면에서 진입 (뒤로가기를 매장 상세로 보냄)
  purchaseTicketSeq?: string; // 이용권 상세에서 "예약하기"로 진입 시 미리 선택할 이용권
  petSeq?: string; // 위 이용권과 함께 미리 선택할 반려견
}

export const Route = createFileRoute(
  "/customer/layout/reservation-with-subscription",
)({
  component: ReservationWithSubscription,
  validateSearch: (
    search: Record<string, unknown>,
  ): ReservationWithSubscriptionParams => {
    return {
      // URL 직렬화 방식에 따라 숫자/문자열 어느 쪽으로 와도 문자열로 정규화
      companySeq:
        search.companySeq != null ? String(search.companySeq) : undefined,
      cdProductType: search.cdProductType as string,
      restoreState: search.restoreState as string,
      fromPayment:
        search.fromPayment != null ? String(search.fromPayment) : undefined,
      purchaseTicketSeq:
        search.purchaseTicketSeq != null
          ? String(search.purchaseTicketSeq)
          : undefined,
      petSeq: search.petSeq != null ? String(search.petSeq) : undefined,
    };
  },
});

interface UserSubscription {
  subscriptionSeq: number;
  planSeq: number;
  planName: string;
  remainingCount?: number; // 횟수이용권인 경우 남은 횟수
  isCountBased: boolean;
  totalCount?: number; // 횟수이용권인 경우 총 횟수
  purchaseDate: string;
  expiryDate?: string;
  cdProductType?: string;
  ticketCount?: number; // totalCount가 1인 티켓의 장수
  petSeq?: number; // 펫 단위 이용권의 경우 귀속된 반려동물 식별자
  originalTickets?: TicketListItem[]; // 원본 티켓 데이터들
  minUseQty?: number | null; // 데이케어 시간권: 최소 이용 수량(시간)
}

function ReservationWithSubscription() {
  const {
    companySeq,
    cdProductType,
    restoreState,
    fromPayment,
    purchaseTicketSeq,
    petSeq: petSeqParam,
  } = Route.useSearch();
  const companySeqNumber = companySeq ? Number.parseInt(companySeq, 10) : 0;
  const navigate = useNavigate();
  const [selectedSubscription, setSelectedSubscription] =
    useState<UserSubscription | null>(null);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string | null>(
    null,
  );
  // const [isDayOfWeekListExpanded, setIsDayOfWeekListExpanded] = useState(true);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [isStartTimeListExpanded, setIsStartTimeListExpanded] = useState(true);
  const [isEndTimeListExpanded, setIsEndTimeListExpanded] = useState(true);
  const [isSubscriptionListExpanded, setIsSubscriptionListExpanded] =
    useState(true);
  const [selectedPet, setSelectedPet] = useState<PetDataType | null>(null);
  // 예약 플로우 동안 하단 nav 숨김 — 조건(이용권+반려동물 선택)만 되면 예약하기 버튼 상시 노출
  useHideNav();
  const [isDateLimitModalOpen, setIsDateLimitModalOpen] = useState(false);
  const [dateLimitInfo, setDateLimitInfo] = useState<{
    maxCount: number;
    selectedPlansArray: Array<{ planSeq: number; quantity: number }>;
    companySeq?: string;
  } | null>(null);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  // 예약 게이트에서 ensure(find-or-autosend)로 확보한 서명 대상 requestSeq
  const [ensuredRequestSeq, setEnsuredRequestSeq] = useState<number | null>(
    null,
  );
  const ensureContractMutation = useEnsureContractRequest();
  const [showReservationCompleteDialog, setShowReservationCompleteDialog] =
    useState(false);
  // 중복 예약 안내 다이얼로그 상태
  const [duplicateReserveInfo, setDuplicateReserveInfo] = useState<{
    mode: "same" | "other";
    conflictCompanyName: string;
  } | null>(null);
  // forceProceed 재시도용 마지막 요청 저장
  const lastReserveRequestRef = useRef<any>(null);
  // 유치원 정원 초과 안내: 초과 날짜(YYYY-MM-DD)와 그 시점의 선택 날짜 수
  //  - 단일 날짜: 확인만 (닫고 날짜 재선택)
  //  - 여러 날짜: [예약 진행](초과 날짜 제외하고 진행) / [날짜 다시 선택하기](초과 날짜 자동 해제)
  const [capacityFull, setCapacityFull] = useState<{
    fullDates: string[];
    selectedCount: number;
  } | null>(null);
  // 호텔 전용: 시작 시간 (시/분 분리)
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<string | null>(null);
  // 데이케어 시간권: 이용시간(시간 수)
  const [selectedUseHours, setSelectedUseHours] = useState<number | null>(null);

  // 날짜 제한 모달 확인 핸들러
  const handleDateLimitModalConfirm = useCallback(() => {
    if (dateLimitInfo) {
      // 선택 상태 저장 (복원을 위해)
      const restoreState = {
        subscriptionSeq: selectedSubscription?.subscriptionSeq,
        petSeq: selectedPet?.petSeq,
        selectedDates: selectedDates.map((date) => date.toISOString()),
        startTime: startTime,
        endTime: endTime,
      };

      console.log("상태 저장:", restoreState);

      navigate({
        to: "/customer/layout/subscription-list",
        search: {
          companySeq: dateLimitInfo.companySeq,
          selectedPlans: JSON.stringify(dateLimitInfo.selectedPlansArray),
          restoreState: JSON.stringify(restoreState),
        },
      });
    }
    setIsDateLimitModalOpen(false);
    setDateLimitInfo(null);
  }, [
    dateLimitInfo,
    selectedSubscription,
    selectedPet,
    selectedDates,
    startTime,
    endTime,
    navigate,
  ]);

  // 커스텀 뒤로가기 핸들러
  const handleBackClick = useCallback(() => {
    // 일반 진입(상세 → 예약)은 브라우저 back으로 복귀해야 히스토리에 중복 엔트리가 안 생긴다.
    // 단, 아래 두 경우는 back이 결제 흐름으로 되돌아가므로 매장 상세로 명시 이동한다.
    //  - restoreState: 이용권 구매 복원 흐름으로 재진입 → back은 구매 화면
    //  - fromPayment: 결제 완료 화면의 "예약하기"로 진입 → back은 결제 성공 화면
    //    (성공 화면 재진입 시 order-confirm 이 재호출돼 실패 화면으로 튄다)
    // 딥링크(스택 없음)도 명시 이동 폴백.
    if (!restoreState && !fromPayment && window.history.length > 1) {
      window.history.back();
      return;
    }
    if (companySeqNumber) {
      navigate({
        to: "/customer/layout/home-serach-detail",
        // 문자열로 넘기면 JSON 직렬화로 URL이 %22145%22 가 되므로 숫자로 전달
        search: { companySeq: companySeqNumber as unknown as string },
        replace: true, // 히스토리에 새 항목 추가하지 않고 현재 항목 교체
      });
    } else {
      window.history.back();
    }
  }, [restoreState, fromPayment, companySeqNumber, navigate]);

  // 반려견 목록 가져오기 (예약용 API 사용)
  const {
    data: petList = [],
    isLoading: isPetLoading,
    refetch: refetchPetList,
  } = useReservationPetList(companySeqNumber, !!companySeqNumber);

  // 이용권 리스트 가져오기
  const { data: ticketList = [], isLoading: isTicketLoading } = useTicketList(
    true,
    companySeqNumber || undefined,
  );

  // 선택된 이용권의 cdProductType 가져오기
  const selectedCdProductType = useMemo(() => {
    return selectedSubscription?.cdProductType || cdProductType || "";
  }, [selectedSubscription, cdProductType]);

  // 호텔 여부 / 박수
  const isHotel = selectedCdProductType === "PRODUCT_TYPE_HOTEL";
  const hotelTotalDays = selectedSubscription?.totalCount ?? 0;
  // 데이케어 시간권: 단일 날짜 + 시작시간(정시) + 이용시간(시간 수)
  const isDaycare = selectedCdProductType === "PRODUCT_TYPE_DAYCARE";
  // 데이케어 이용시간 Select 범위: 최소 = 이용권 최소 이용 수량(없으면 1), 최대 = 잔여 시간
  const daycareMinUseHours = selectedSubscription?.minUseQty ?? 1;
  const daycareMaxUseHours = selectedSubscription?.remainingCount ?? 0;
  const daycareUseHoursOptions = useMemo(() => {
    if (!isDaycare || daycareMaxUseHours < daycareMinUseHours) {
      return [] as number[];
    }
    return Array.from(
      { length: daycareMaxUseHours - daycareMinUseHours + 1 },
      (_, i) => daycareMinUseHours + i,
    );
  }, [isDaycare, daycareMinUseHours, daycareMaxUseHours]);

  // 서비스 타입별 예약 완료 메시지
  const getReservationCompleteMessage = (productType: string) => {
    switch (productType) {
      case "PRODUCT_TYPE_KINDERGARTEN":
        return "수업예약이 접수되었습니다.";
      case "PRODUCT_TYPE_DAYCARE":
        return "데이케어 서비스가 접수되었습니다.";
      case "PRODUCT_TYPE_HOTEL":
        return "호텔 서비스가 접수되었습니다.";
      case "PRODUCT_TYPE_GROOMING":
        return "미용 서비스가 접수되었습니다.";
      default:
        return "예약이 접수되었습니다.";
    }
  };

  // 예약 폼 조회 (반려견과 회사가 선택되었을 때만 호출)
  const { data: reserveForms } = useReserveForms(
    companySeqNumber,
    selectedPet?.petSeq || 0,
    selectedCdProductType,
    !!(companySeqNumber && selectedPet?.petSeq && selectedCdProductType),
  );

  // 예약 생성 API 훅
  const { mutate: createReserve, isPending: isReserving } = useCreateReserve();

  // API 응답 데이터를 UserSubscription 타입으로 변환
  const subscriptions: UserSubscription[] = useMemo(() => {
    if (!ticketList || ticketList.length === 0) {
      return [];
    }

    // companySeq가 있으면 해당 회사의 이용권만 필터링
    const filteredTickets = companySeqNumber
      ? ticketList.filter(
          (ticket: TicketListItem) => ticket.companySeq === companySeqNumber,
        )
      : ticketList;

    // 동일 이용권(productTicketSeq)을 묶지 않고, 보유한 티켓을 1장씩 개별로 표시
    return filteredTickets.map((ticket: TicketListItem) => ({
      subscriptionSeq: ticket.purchaseTicketSeq,
      planSeq: ticket.productTicketSeq,
      planName: ticket.name,
      remainingCount: ticket.remainCount,
      isCountBased: ticket.splitYn === "Y",
      totalCount: ticket.totalCount,
      purchaseDate: ticket.purchaseDate,
      expiryDate: ticket.expiredDate,
      cdProductType: ticket.cdProductType,
      petSeq: ticket.petSeq, // 펫 단위 이용권: 귀속된 반려동물
      originalTickets: [ticket], // 원본 티켓 데이터 보관
      minUseQty: ticket.minUseQty ?? null,
    }));
  }, [ticketList, companySeqNumber]);

  // 선택된 펫에 귀속된 이용권만 필터링
  // - selectedPet이 없으면 빈 배열
  // - 이용권에 petSeq가 지정되어 있으면 해당 펫과 일치하는 것만 노출
  // - petSeq가 없는 이용권(보호자 단위)은 펫 선택과 무관하게 노출
  const filteredSubscriptions = useMemo(() => {
    if (!selectedPet) return [];
    return subscriptions.filter(
      (sub) => sub.petSeq == null || sub.petSeq === selectedPet.petSeq,
    );
  }, [subscriptions, selectedPet]);

  // 펫이 1마리만 있으면 자동 선택
  useEffect(() => {
    if (!selectedPet && petList.length === 1) {
      setSelectedPet(petList[0]);
    }
  }, [petList, selectedPet]);

  // 펫 변경 시 이전에 선택된 이용권이 더 이상 호환되지 않으면 해제
  useEffect(() => {
    if (selectedSubscription && selectedPet) {
      if (
        selectedSubscription.petSeq != null &&
        selectedSubscription.petSeq !== selectedPet.petSeq
      ) {
        setSelectedSubscription(null);
        setIsSubscriptionListExpanded(true);
      }
    } else if (!selectedPet && selectedSubscription) {
      // 펫 해제 시 이용권도 해제
      setSelectedSubscription(null);
      setIsSubscriptionListExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPet]);

  // 펫 + 이용권 둘 다 선택 + 이용권이 현재 펫과 호환될 때만 계약 다이얼로그 표시
  // (호환 체크가 없으면 펫 변경 직후 이전 펫의 이용권 상태로 다이얼로그가 잘못 뜸 -
  //  setSelectedSubscription(null)이 다음 렌더에 반영되기 전 시점에 효과가 먼저 실행되기 때문)
  useEffect(() => {
    if (!selectedPet || !selectedSubscription) return;
    const isCompatible =
      selectedSubscription.petSeq == null ||
      selectedSubscription.petSeq === selectedPet.petSeq;
    if (isCompatible && selectedPet.hasContract === false) {
      setShowContractDialog(true);
    }
  }, [selectedPet, selectedSubscription]);

  // 이용권 상세 → "예약하기" 진입: 해당 이용권/반려견을 미리 선택해 둔다.
  // (반려견을 먼저 정해야 이용권 목록이 노출되므로 둘을 같은 렌더에서 함께 세팅한다)
  const hasPreselected = useRef(false);
  useEffect(() => {
    if (!purchaseTicketSeq || restoreState) return;
    if (hasPreselected.current) return;
    if (subscriptions.length === 0 || petList.length === 0) return;

    hasPreselected.current = true;

    const targetSeq = Number.parseInt(purchaseTicketSeq, 10);
    const subscription = subscriptions.find(
      (sub) => sub.subscriptionSeq === targetSeq,
    );
    if (!subscription) {
      // 만료/소진 등으로 예약 가능한 이용권 목록에 없는 경우 — 사용자가 직접 고르게 둔다
      console.warn("사전 선택할 이용권을 찾지 못했습니다:", targetSeq);
      return;
    }

    // 반려견: 파라미터 > 이용권 귀속 반려견 > 1마리뿐이면 그 반려견
    const petSeqCandidate =
      (petSeqParam ? Number.parseInt(petSeqParam, 10) : null) ||
      subscription.petSeq ||
      (petList.length === 1 ? petList[0].petSeq : null);
    const pet = petSeqCandidate
      ? petList.find((p: PetDataType) => p.petSeq === petSeqCandidate)
      : undefined;
    if (!pet) return;

    setSelectedPet(pet);
    setSelectedSubscription(subscription);
    setIsSubscriptionListExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseTicketSeq, petSeqParam, restoreState, subscriptions, petList]);

  // 선택 상태 복원 (restoreState 파라미터가 있을 때)
  const hasRestoredState = useRef(false);
  useEffect(() => {
    if (
      restoreState &&
      !hasRestoredState.current &&
      subscriptions.length > 0 &&
      petList.length > 0
    ) {
      try {
        console.log("복원 시도 - restoreState:", restoreState);
        // restoreState 파싱 (TanStack Router가 자동으로 디코딩하므로 직접 파싱)
        let restoredState;
        try {
          // 먼저 그대로 파싱 시도
          restoredState = JSON.parse(restoreState);
        } catch {
          // 실패 시 디코딩 후 파싱 시도
          try {
            restoredState = JSON.parse(decodeURIComponent(restoreState));
          } catch (e) {
            console.error("restoreState 파싱 실패:", e);
            return;
          }
        }
        console.log("복원할 상태:", restoredState);

        // 이용권 복원
        if (restoredState.subscriptionSeq) {
          const subscription = subscriptions.find(
            (sub) => sub.subscriptionSeq === restoredState.subscriptionSeq,
          );
          console.log("이용권 찾기:", {
            찾는Seq: restoredState.subscriptionSeq,
            찾은이용권: subscription,
            전체이용권: subscriptions.map((s) => s.subscriptionSeq),
          });
          if (subscription) {
            setSelectedSubscription(subscription);
            setIsSubscriptionListExpanded(false);
            console.log("이용권 복원 완료");
          } else {
            console.warn(
              "이용권을 찾을 수 없습니다:",
              restoredState.subscriptionSeq,
            );
          }
        }

        // 반려견 복원
        if (restoredState.petSeq) {
          const pet = petList.find(
            (p: PetDataType) => p.petSeq === restoredState.petSeq,
          );
          console.log("반려견 찾기:", {
            찾는Seq: restoredState.petSeq,
            찾은반려견: pet,
            전체반려견: petList.map((p: PetDataType) => p.petSeq),
          });
          if (pet) {
            // 펫 단위 이용권 도입 후에는 selectedPet이 useEffect에서
            // selectedSubscription.petSeq 기반으로 자동 동기화되지만,
            // 이용권에 petSeq가 없는 과거 데이터 호환을 위해 직접 세팅도 유지
            setSelectedPet(pet);
            console.log("반려견 복원 완료");
          } else {
            console.warn("반려견을 찾을 수 없습니다:", restoredState.petSeq);
          }
        }

        // 날짜 복원
        if (
          restoredState.selectedDates &&
          Array.isArray(restoredState.selectedDates)
        ) {
          const dates = restoredState.selectedDates.map(
            (dateStr: string) => new Date(dateStr),
          );
          setSelectedDates(dates);
          console.log("날짜 복원 완료:", dates);
        }

        // 시작 시간 복원
        if (restoredState.startTime) {
          setStartTime(restoredState.startTime);
          setIsStartTimeListExpanded(false);
          console.log("시작 시간 복원 완료:", restoredState.startTime);
        }

        // 종료 시간 복원
        if (restoredState.endTime) {
          setEndTime(restoredState.endTime);
          setIsEndTimeListExpanded(false);
          console.log("종료 시간 복원 완료:", restoredState.endTime);
        }

        hasRestoredState.current = true;
        console.log("상태 복원 완료");
      } catch (error) {
        console.error("선택 상태 복원 실패:", error);
      }
    } else if (restoreState && !hasRestoredState.current) {
      console.log("복원 대기 중:", {
        restoreState: !!restoreState,
        subscriptionsLength: subscriptions.length,
        petListLength: petList.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreState, subscriptions.length, petList.length]);

  const isLoading = isTicketLoading;

  // 날짜 포맷팅 (YYYY-MM-DD 형식 - 예약 API용)
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // yyyyMM 형식으로 변환 (예약 가능 날짜 API용)
  const formatYearMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}${month}`;
  };

  // yyyyMMdd 형식으로 변환 (예약 가능 시간 API용)
  const formatYearMonthDay = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
  };

  // 현재 월의 예약 가능 날짜 조회 (달력의 달 변경 시 업데이트)
  const [currentMonth, setCurrentMonth] = useState(() => {
    return formatYearMonth(new Date());
  });

  const { data: reservableDateData } = useReservableDate(
    companySeqNumber,
    currentMonth,
    !!(companySeqNumber && selectedPet),
    selectedCdProductType || undefined,
    selectedPet?.petSeq || undefined,
  );

  // 캘린더 화면과 동일한 /api/user/calendar 호출
  // currentMonth(yyyyMM) → yyyyMMdd 의 1일자로 변환하여 조회
  const { data: userCalendarData } = useCalendarList({
    petSeq: selectedPet?.petSeq ? String(selectedPet.petSeq) : "",
    yearMonthDay: currentMonth ? `${currentMonth}01` : "",
  });

  // 디버깅: currentMonth 변경 추적
  useEffect(() => {
    console.log(
      "[API 호출] currentMonth 변경됨:",
      currentMonth,
      "companySeq:",
      companySeqNumber,
      "selectedPet:",
      !!selectedPet,
    );
  }, [currentMonth, companySeqNumber, selectedPet]);

  // 달력의 달 변경 핸들러
  const handleCalendarMonthChange = useCallback((isNext: boolean) => {
    // 함수형 업데이트를 사용하여 항상 최신 currentMonth 값을 사용
    setCurrentMonth((prevMonth) => {
      console.log("[달 변경] 이전 월:", prevMonth, "isNext:", isNext);

      // 현재 표시 중인 월 계산
      const regex = /(\d{4})(\d{2})/;
      const match = regex.exec(prevMonth);
      const yearStr = match?.[1];
      const monthStr = match?.[2];

      if (!yearStr || !monthStr) {
        // 파싱 실패 시 현재 날짜 사용
        const today = new Date();
        const newDate = new Date(
          today.getFullYear(),
          today.getMonth() + (isNext ? 1 : -1),
          1,
        );
        const newMonthStr = formatYearMonth(newDate);
        console.log("[달 변경] 파싱 실패, 새 월:", newMonthStr);
        return newMonthStr;
      }

      const displayYear = Number.parseInt(yearStr, 10);
      const displayMonth = Number.parseInt(monthStr, 10) - 1; // 0-based month

      // 다음/이전 월 계산
      let newYear = displayYear;
      let newMonth = displayMonth;

      if (isNext) {
        newMonth += 1;
        if (newMonth > 11) {
          newMonth = 0;
          newYear += 1;
        }
      } else {
        newMonth -= 1;
        if (newMonth < 0) {
          newMonth = 11;
          newYear -= 1;
        }
      }

      const newDate = new Date(newYear, newMonth, 1);
      const newMonthStr = formatYearMonth(newDate);
      console.log(
        "[달 변경] 새 월:",
        newMonthStr,
        "년:",
        newYear,
        "월:",
        newMonth,
      );
      return newMonthStr;
    });
  }, []);

  // 예약 불가능한 날짜 추출 (calendar 데이터에서 예약 가능한 날짜가 아닌 날짜들 + 같은 그룹 중복 예약일)
  const disabledDates = useMemo(() => {
    if (!reservableDateData?.calendar) {
      return [];
    }

    const calendar = reservableDateData.calendar;
    const reservedDates = reservableDateData.reservedDates ?? [];
    const reservedDateSet = new Set(reservedDates);
    const disabled: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // currentMonth에서 년도와 월 추출 (yyyyMM 형식)
    const regex = /(\d{4})(\d{2})/;
    const match = regex.exec(currentMonth);
    const yearStr = match?.[1];
    const monthStr = match?.[2];
    if (!yearStr || !monthStr) {
      return [];
    }

    const displayYear = Number.parseInt(yearStr, 10);
    const displayMonth = Number.parseInt(monthStr, 10) - 1; // 0-based month

    // 현재 표시 중인 월의 모든 날짜를 순회하면서 예약 불가능한 날짜 찾기
    const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayYear, displayMonth, day);
      date.setHours(0, 0, 0, 0);
      const dateString = formatYearMonthDay(date);

      // calendar 데이터 구조 확인
      // calendar가 객체인 경우: 키가 날짜 문자열이고 값이 true/false 또는 예약 가능 여부
      // calendar가 배열인 경우: 예약 가능한 날짜 문자열 배열
      let isReservable = false;

      if (Array.isArray(calendar)) {
        // 배열인 경우: 예약 가능한 날짜 문자열이 포함되어 있으면 true
        isReservable = calendar.includes(dateString);
      } else if (typeof calendar === "object" && calendar !== null) {
        // 객체인 경우: 해당 날짜 키가 있고 값이 truthy이면 예약 가능
        const dateValue = calendar[dateString];
        isReservable =
          dateValue !== undefined && dateValue !== false && dateValue !== null;
      }

      // 영업일이 아니거나 같은 그룹·같은 펫으로 이미 예약된 날짜이면 disabled
      const isDuplicateReserved = reservedDateSet.has(dateString);
      if ((!isReservable || isDuplicateReserved) && date >= today) {
        disabled.push(date);
      }
    }

    return disabled;
  }, [reservableDateData, currentMonth]);

  // 선택한 모든 날짜의 예약 가능 시간 조회 (yyyyMMdd 형식)
  const selectedDateStrings = useMemo(() => {
    return selectedDates.map((date) => formatYearMonthDay(date));
  }, [selectedDates]);

  // 모든 선택된 날짜에 대해 예약 가능 시간 조회
  // [비활성화] 비호텔 시작/종료 시간 UI가 현재 비노출 상태이며,
  // useReservableTime와 동일 queryKey를 사용해 캐시 형태 충돌(호텔 시간 옵션 누락) 원인이라 enabled: false로 차단.
  const reservableTimeQueries = useQueries({
    queries: selectedDateStrings.map((dateString) => ({
      queryKey: ["reservable-time", companySeqNumber, dateString],
      queryFn: async () => {
        const response = await getReservableTime({
          companySeq: companySeqNumber,
          yearMonthDay: dateString,
        });
        return response.data || null;
      },
      enabled: false,
      staleTime: 5 * 60 * 1000, // 5분
    })),
  });

  // 모든 날짜의 예약 가능 시간 데이터를 합쳐서 최소 시작시간, 최대 종료시간 계산
  const reservableTimeData = useMemo(() => {
    const validData = reservableTimeQueries
      .map((query) => query.data)
      .filter(
        (data): data is { calendar: any; startTime: string; endTime: string } =>
          !!data && !!data.startTime && !!data.endTime,
      );

    if (validData.length === 0) {
      return null;
    }

    // 모든 날짜의 시작시간 중 최소값, 종료시간 중 최대값 찾기
    const firstData = validData[0];
    if (!firstData || !firstData.startTime || !firstData.endTime) {
      return null;
    }

    let minStartTime = firstData.startTime;
    let maxEndTime = firstData.endTime;

    validData.forEach((data) => {
      if (data.startTime < minStartTime) {
        minStartTime = data.startTime;
      }
      if (data.endTime > maxEndTime) {
        maxEndTime = data.endTime;
      }
    });

    return {
      startTime: minStartTime,
      endTime: maxEndTime,
    };
  }, [reservableTimeQueries]);

  // 시간 옵션 생성 (예약 가능 시간 기반)
  const timeOptions = useMemo(() => {
    if (!reservableTimeData?.startTime || !reservableTimeData?.endTime) {
      // 예약 가능 시간이 없으면 기본값 (11:00 ~ 20:30, 30분 간격)
      const options: string[] = [];
      for (let hour = 11; hour <= 20; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          if (hour === 20 && minute > 0) break; // 20:30까지만
          const timeValue = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
          options.push(timeValue);
        }
      }
      return options;
    }

    // 예약 가능 시간 범위 내에서 30분 간격으로 생성
    const options: string[] = [];
    const startTime = reservableTimeData.startTime;
    const endTime = reservableTimeData.endTime;

    // 시작 시간과 종료 시간을 분으로 변환
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    // 30분 간격으로 시간 생성
    for (
      let totalMinutes = startTotalMinutes;
      totalMinutes <= endTotalMinutes;
      totalMinutes += 30
    ) {
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;
      const timeValue = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      options.push(timeValue);
    }

    return options;
  }, [reservableTimeData]);

  // 오전/오후로 시간 분류
  const { amTimes, pmTimes } = useMemo(() => {
    const amTimes: string[] = [];
    const pmTimes: string[] = [];

    timeOptions.forEach((time) => {
      const [hour] = time.split(":").map(Number);
      if (hour < 12) {
        amTimes.push(time);
      } else {
        pmTimes.push(time);
      }
    });

    return { amTimes, pmTimes };
  }, [timeOptions]);

  // 호텔 전용: 첫 선택일 기준 예약 가능 시간 조회 → 시(時) 옵션
  const firstSelectedDate = selectedDates[0] ?? null;
  const firstDateKey = firstSelectedDate
    ? formatYearMonthDay(firstSelectedDate)
    : "";
  const { data: hotelReservableTime, isFetching: isHotelTimeFetching } =
    useReservableTime(
      companySeqNumber,
      firstDateKey,
      (isHotel || isDaycare) && !!firstSelectedDate && !!companySeqNumber,
    );
  const hotelHourOptions = useMemo(() => {
    if (!hotelReservableTime?.startTime || !hotelReservableTime?.endTime) {
      return [] as string[];
    }
    const start = Number.parseInt(
      hotelReservableTime.startTime.split(":")[0] ?? "0",
      10,
    );
    const end = Number.parseInt(
      hotelReservableTime.endTime.split(":")[0] ?? "0",
      10,
    );
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];
    const list: string[] = [];
    for (let h = start; h <= end; h++) {
      list.push(String(h).padStart(2, "0"));
    }
    return list;
  }, [hotelReservableTime?.startTime, hotelReservableTime?.endTime]);
  const hotelMinuteOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => String(i * 10).padStart(2, "0")),
    [],
  );

  // 호텔/데이케어: 시작일이 바뀔 때마다 선택한 시간 초기화
  useEffect(() => {
    if (!isHotel && !isDaycare) return;
    setSelectedHour(null);
    setSelectedMinute(null);
    setSelectedUseHours(null);
  }, [isHotel, isDaycare, firstDateKey]);

  // 최대 선택 가능한 날짜 수 계산
  const maxSelectableDates = useMemo(() => {
    if (!selectedSubscription) return null;

    // 데이케어: 하루 단위 시간 예약이므로 단일 날짜만 선택
    if (isDaycare) return 1;

    // totalCount가 2 이상인 경우: remainingCount만큼만 선택 가능
    if (
      selectedSubscription.totalCount &&
      selectedSubscription.totalCount >= 2
    ) {
      return selectedSubscription.remainingCount || 0;
    }

    // 그룹화한 티켓(totalCount가 1이고 ticketCount가 있는 경우) 또는 단일 티켓
    // ticketCount가 있으면 그 값, 없으면 1
    return selectedSubscription.ticketCount || 1;
  }, [selectedSubscription]);

  // 동일 일자 비교
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  // 날짜 선택 핸들러 (여러 날짜 선택)
  const handleDateChange = (days: Date[] | Date | null) => {
    if (days === null) {
      setSelectedDates([]);
      return;
    }

    let datesArray: Date[];
    if (Array.isArray(days)) {
      datesArray = days;
    } else {
      // 단일 날짜인 경우 배열로 변환
      datesArray = [days];
    }

    // 데이케어: 마지막으로 클릭한 단일 날짜만 선택
    if (isDaycare) {
      const newDate = datesArray.find(
        (d) => !selectedDates.some((sd) => isSameDay(sd, d)),
      );
      if (!newDate) {
        setSelectedDates([]);
        setSelectedDayOfWeek(null);
        return;
      }
      setSelectedDates([newDate]);
      setSelectedDayOfWeek(null);
      return;
    }

    // 호텔: 새로 클릭한 날짜를 체크인으로, (박수+1)일 연속 자동 선택
    // 예) 1박 → 체크인+체크아웃 2일 표시, 2박 → 3일 표시
    if (isHotel) {
      const newDate = datesArray.find(
        (d) => !selectedDates.some((sd) => isSameDay(sd, d)),
      );
      if (!newDate) {
        // 기존 선택일 토글 → 전체 해제
        setSelectedDates([]);
        setSelectedDayOfWeek(null);
        return;
      }
      const nights = hotelTotalDays > 0 ? hotelTotalDays : 1;
      const filled: Date[] = [];
      for (let i = 0; i <= nights; i++) {
        const d = new Date(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate() + i,
        );
        filled.push(d);
      }
      setSelectedDates(filled);
      setSelectedDayOfWeek(null);
      return;
    }

    // 최대 선택 가능 날짜 수 검증
    if (maxSelectableDates !== null && datesArray.length > maxSelectableDates) {
      // 최대 개수를 초과한 경우
      const selectedDatesCount = datesArray.length;
      const maxCount = maxSelectableDates;

      // 남은 횟수가 전체와 같은 경우(예: 8/8회)에는 이용권 구매 모달을 띄우지 않음
      const isFullRemainingCount =
        selectedSubscription?.isCountBased &&
        selectedSubscription?.remainingCount ===
          selectedSubscription?.totalCount;

      if (selectedSubscription && !isFullRemainingCount) {
        // 추가로 필요한 날짜 수
        const additionalDatesNeeded = selectedDatesCount - maxCount;

        // 필요한 티켓 수량 계산
        // totalCount가 2 이상인 경우: totalCount로 나눈 올림 값
        // totalCount가 1인 경우 (그룹화한 티켓 또는 단일 티켓): 추가 날짜 수만큼
        let quantity = 1;
        if (
          selectedSubscription.totalCount &&
          selectedSubscription.totalCount >= 2
        ) {
          quantity = Math.ceil(
            additionalDatesNeeded / selectedSubscription.totalCount,
          );
        } else {
          quantity = additionalDatesNeeded;
        }

        const selectedPlansArray = [
          {
            planSeq: selectedSubscription.planSeq,
            quantity: quantity,
          },
        ];

        // 모달 표시 및 이동 정보 저장
        setDateLimitInfo({
          maxCount,
          selectedPlansArray,
          companySeq: companySeq ? String(companySeqNumber) : undefined,
        });
        setIsDateLimitModalOpen(true);
      }

      // 최대 개수만큼만 선택
      setSelectedDates(datesArray.slice(0, maxSelectableDates));
      setSelectedDayOfWeek(null);
      return;
    }

    setSelectedDates(datesArray);
    setSelectedDayOfWeek(null); // 날짜 선택 시 요일 초기화
  };

  // 이용권 선택 핸들러
  const handleSubscriptionSelect = (subscription: UserSubscription) => {
    if (
      selectedSubscription?.subscriptionSeq === subscription.subscriptionSeq
    ) {
      // 같은 이용권을 다시 클릭하면 목록 펼치기 (펫 선택은 유지)
      setIsSubscriptionListExpanded(true);
      setSelectedSubscription(null);
      setSelectedDates([]);
      setSelectedDayOfWeek(null);
      setStartTime(null);
      setEndTime(null);
      setIsStartTimeListExpanded(true);
      setIsEndTimeListExpanded(true);
    } else {
      // 다른 이용권 선택 시 목록 접기 (펫 선택은 유지)
      setSelectedSubscription(subscription);
      setIsSubscriptionListExpanded(false);
      setSelectedDates([]);
      setSelectedDayOfWeek(null);
      setStartTime(null);
      setEndTime(null);
      setIsStartTimeListExpanded(true);
      setIsEndTimeListExpanded(true);
    }
  };

  // 선택된 이용권 클릭 시 목록 펼치기
  const handleSelectedSubscriptionClick = () => {
    setIsSubscriptionListExpanded(true);
  };

  // 시작 시간 선택 목록 펼치기/접기 핸들러
  const handleStartTimeListToggle = () => {
    setIsStartTimeListExpanded(!isStartTimeListExpanded);
  };

  // 종료 시간 선택 목록 펼치기/접기 핸들러
  const handleEndTimeListToggle = () => {
    setIsEndTimeListExpanded(!isEndTimeListExpanded);
  };

  // 요일 선택 목록 펼치기/접기 핸들러
  // const handleDayOfWeekListToggle = () => {
  //   setIsDayOfWeekListExpanded(!isDayOfWeekListExpanded);
  // };

  // 시간 선택 핸들러
  const handleTimeSelect = (time: string, isStart: boolean) => {
    if (isStart) {
      setStartTime(time);
      setIsStartTimeListExpanded(false);
      // 시작 시간이 종료 시간보다 크거나 같으면 종료 시간 초기화
      if (endTime && time >= endTime) {
        setEndTime(null);
      }
    } else {
      // 종료 시간은 시작 시간보다 커야 함
      if (startTime && time <= startTime) {
        alert("종료 시간은 시작 시간보다 늦어야 합니다.");
        return;
      }
      setEndTime(time);
      setIsEndTimeListExpanded(false);
    }
  };

  // 예약하기 핸들러
  //  opts.dates            : 이 날짜들로 예약(정원 초과 날짜 제외 진행용). 미지정이면 selectedDates.
  //  opts.skipCapacityCheck: 정원 팝업에서 [예약 진행] 을 눌러 재호출할 때 true.
  const handleReservation = async (opts?: {
    dates?: Date[];
    skipCapacityCheck?: boolean;
  }) => {
    const reserveDates = opts?.dates ?? selectedDates;
    if (isReserving) return; // 연타로 인한 중복 예약 방지
    if (!selectedSubscription) {
      alert("이용권을 선택해주세요.");
      return;
    }
    if (!selectedPet) {
      alert("반려견을 선택해주세요.");
      return;
    }
    if (reserveDates.length === 0) {
      alert("날짜를 선택해주세요.");
      return;
    }
    if (!reserveForms) {
      alert("예약 폼 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }
    if (!selectedCdProductType) {
      alert("상품 타입 정보가 없습니다.");
      return;
    }

    // gradeConfirm 체크
    if (!reserveForms.gradeConfirm) {
      alert("해당 반려견의 체급은 수용이 불가능합니다.");
      return;
    }

    // 호텔: 시작 시간 검증
    if (isHotel && (!selectedHour || !selectedMinute)) {
      alert("시작 시간을 선택해주세요.");
      return;
    }

    // 데이케어: 시작 시간/이용시간 검증
    if (isDaycare && !selectedHour) {
      alert("입소 시간을 선택해주세요.");
      return;
    }
    if (isDaycare && !selectedUseHours) {
      alert("이용시간을 선택해주세요.");
      return;
    }

    // tickets 배열 생성
    // totalCount가 1인 경우 (그룹화된 티켓 포함): originalTickets를 사용하여 실제 티켓들을 보냄
    // totalCount가 2 이상인 경우: 하나의 티켓으로 여러 일수를 담당할 수 있음
    // 유치원(등원)·데이케어 정원 체크 — 정원 제한이 켜진 매장에서 초과 날짜가 있으면 팝업 후 중단.
    // 카운트 기준은 예약 요청 건(확정 아님 포함). 체크 API 실패 시엔 안전하게 예약을 그대로 진행한다.
    const isKindergarten = selectedCdProductType === "PRODUCT_TYPE_KINDERGARTEN";
    if ((isKindergarten || isDaycare) && !opts?.skipCapacityCheck) {
      const dateStrs = reserveDates.map((d) => formatDate(d));
      try {
        const cap = await checkReservationCapacity(companySeqNumber, dateStrs);
        if (cap.limitEnabled && cap.fullDates.length > 0) {
          setCapacityFull({
            fullDates: cap.fullDates,
            selectedCount: reserveDates.length,
          });
          return;
        }
      } catch (e) {
        console.warn("정원 체크 실패 — 예약 진행", e);
      }
    }

    let tickets: Array<{
      purchaseTicketSeq: number;
      productTicketSeq: number;
      paymentTicket: number;
    }> = [];
    const selectedDaysCount = reserveDates.length;

    if (isDaycare) {
      // 데이케어: 1개 이용권을 이용시간(시간 수)만큼 차감
      tickets = [
        {
          purchaseTicketSeq: selectedSubscription.subscriptionSeq,
          productTicketSeq: selectedSubscription.planSeq,
          paymentTicket: selectedUseHours ?? 0,
        },
      ];
    } else if (isHotel) {
      // 호텔: 1개 티켓이 박수(totalCount)만큼 차감. 캘린더는 박수+1일 표시되지만
      // 결제는 박수만큼만 이뤄짐.
      const nights = hotelTotalDays > 0 ? hotelTotalDays : 1;
      tickets = [
        {
          purchaseTicketSeq: selectedSubscription.subscriptionSeq,
          productTicketSeq: selectedSubscription.planSeq,
          paymentTicket: nights,
        },
      ];
    } else if (selectedSubscription.totalCount === 1) {
      // totalCount가 1인 경우: 각 티켓이 1일을 담당
      if (
        selectedSubscription.originalTickets &&
        selectedSubscription.originalTickets.length > 0
      ) {
        // 그룹화된 티켓: originalTickets를 사용하여 실제 티켓들을 보냄
        // 만료일이 가장 가까운 티켓부터 사용하도록 정렬
        const sortedTickets = [...selectedSubscription.originalTickets].sort(
          (a, b) => {
            const dateA = a.expiredDate
              ? new Date(a.expiredDate).getTime()
              : Infinity;
            const dateB = b.expiredDate
              ? new Date(b.expiredDate).getTime()
              : Infinity;
            return dateA - dateB; // 만료일이 가까운 순서대로 정렬
          },
        );

        // 선택된 날짜 수만큼 티켓 사용 (각 티켓은 1일 담당)
        const availableTickets = sortedTickets.slice(0, selectedDaysCount);

        tickets = availableTickets.map((ticket) => ({
          purchaseTicketSeq: ticket.purchaseTicketSeq,
          productTicketSeq: ticket.productTicketSeq,
          paymentTicket: 1, // 각 티켓은 1일 담당
        }));
      } else {
        // 단일 티켓 (totalCount가 1): 선택된 날짜 수만큼 같은 티켓 사용
        tickets = Array.from({ length: selectedDaysCount }, () => ({
          purchaseTicketSeq: selectedSubscription.subscriptionSeq,
          productTicketSeq: selectedSubscription.planSeq,
          paymentTicket: 1, // 각 날짜마다 1일
        }));
      }
    } else {
      // totalCount가 2 이상인 경우: 하나의 티켓으로 여러 일수를 담당할 수 있음
      // 예: totalCount가 5인 티켓으로 3일 선택하면 1개의 티켓으로 3일 담당
      // 예: totalCount가 5인 티켓으로 7일 선택하면 2개의 티켓 필요 (5일 + 2일)
      const daysPerTicket = selectedSubscription.totalCount || 1;
      const numberOfTickets = Math.ceil(selectedDaysCount / daysPerTicket);

      for (let i = 0; i < numberOfTickets; i++) {
        const startDay = i * daysPerTicket;
        const endDay = Math.min(startDay + daysPerTicket, selectedDaysCount);
        const daysForThisTicket = endDay - startDay;

        tickets.push({
          purchaseTicketSeq: selectedSubscription.subscriptionSeq,
          productTicketSeq: selectedSubscription.planSeq,
          paymentTicket: daysForThisTicket, // 이 티켓이 담당하는 일수
        });
      }
    }

    const petsPayload = petList.map((pet: PetDataType) => ({
      petSeq: pet.petSeq,
      petName: pet.petName,
      select: pet.petSeq === selectedPet.petSeq,
    }));

    // 호텔: dtReservStart/dtReservEnd/startTime/endTime 형식으로 전송
    // - dtReservEnd = 시작일 + totalCount(박수)일
    // - endTime = startTime (totalCount * 24h 뒤 같은 시각)
    const buildReserveForm = () => {
      if (isHotel) {
        const sortedDates = [...reserveDates].sort(
          (a, b) => a.getTime() - b.getTime(),
        );
        const startDate = sortedDates[0];
        const nights =
          hotelTotalDays > 0 ? hotelTotalDays : reserveDates.length || 1;
        const checkoutDate = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate() + nights,
        );
        const startTimeStr = `${selectedHour}:${selectedMinute}:00`;
        return {
          dtReservStart: formatDate(startDate),
          dtReservEnd: formatDate(checkoutDate),
          startTime: startTimeStr,
          endTime: startTimeStr,
          additionals: [],
          pets: petsPayload,
          memo: null,
        };
      }
      if (isDaycare) {
        // 데이케어: 단일 날짜 + 시작시간(정시) + 종료시간(시작 + 이용시간)
        const startDate = reserveDates[0];
        const startTimeStr = `${selectedHour}:00:00`;
        const endTimeStr = `${String(
          Number.parseInt(selectedHour ?? "0", 10) + (selectedUseHours ?? 0),
        ).padStart(2, "0")}:00:00`;
        return {
          dtReservStart: formatDate(startDate),
          startTime: startTimeStr,
          endTime: endTimeStr,
          dayOfWeeks: [],
          additionals: [],
          pets: petsPayload,
          memo: null,
        };
      }
      return {
        dates: reserveDates.map((date) => formatDate(date)),
        dayOfWeeks: reserveForms?.reserveForm?.dayOfWeeks
          ? reserveForms.reserveForm.dayOfWeeks.map((day) => ({
              cdDayOfWeek: day.cdDayOfWeek,
              dayOfWeekName: day.dayOfWeekName,
              select: selectedDayOfWeek === day.cdDayOfWeek,
            }))
          : [],
        additionals: [],
        pets: petsPayload,
        memo: null,
      };
    };
    const reserveForm = buildReserveForm();

    // 예약 생성 요청 데이터 구성
    const requestData = {
      reservationSeq: null,
      cdProductType: selectedCdProductType,
      gradeConfirm: reserveForms.gradeConfirm,
      paymentCoinTotal: null, // 이용권 사용 시 코인 결제 없음
      companySeq: companySeqNumber,
      tickets: tickets,
      reserveForm,
      forceProceed: false,
    };

    console.log("예약 생성 API 호출:", requestData);
    submitReserve(requestData);
  };

  // 예약 API 호출 (forceProceed 재시도에서도 재사용)
  const submitReserve = (requestData: any) => {
    lastReserveRequestRef.current = requestData;
    createReserve(requestData, {
      onSuccess: (response: any) => {
        console.log("예약 생성 응답:", response);
        // 같은 매장 중복 → 안내만 (진행 불가)
        if (response?.statusCode === 4009) {
          setDuplicateReserveInfo({
            mode: "same",
            conflictCompanyName: response?.data?.conflictCompanyName ?? "",
          });
          return;
        }
        // 다른 매장 중복 → 사용자 확인 후 forceProceed 재시도
        if (response?.statusCode === 4010) {
          setDuplicateReserveInfo({
            mode: "other",
            conflictCompanyName: response?.data?.conflictCompanyName ?? "",
          });
          return;
        }
        // 그 외 응답은 기존 흐름 (예약 완료)
        setShowReservationCompleteDialog(true);
      },
      onError: (error: unknown) => {
        console.error("예약 생성 실패:", error);
        alert(
          getErrorMessage(
            error,
            "예약 생성에 실패했습니다. 다시 시도해주세요.",
          ),
        );
      },
    });
  };

  // 다른 매장 중복 안내 팝업에서 "예약 진행" 선택 시
  const handleForceProceedReserve = () => {
    const last = lastReserveRequestRef.current;
    setDuplicateReserveInfo(null);
    if (!last) return;
    submitReserve({ ...last, forceProceed: true });
  };

  if (isLoading) {
    return (
      <>
        <LayoutHeader
          title="예약하기"
          hasBackBtn
          hasFootPrint={false}
          onBackClick={handleBackClick}
        />
        <div className="flex justify-center items-center flex-auto">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader
        title="예약하기"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={handleBackClick}
      />
      <div className="flex flex-col flex-auto bg-[#F5F5F5] pb-24 overflow-x-hidden">
        <div className="px-4 pt-4 pb-6 max-w-full">
          {/* 반려동물 섹션 (먼저 펫을 선택) */}
          <div className="flex flex-col gap-3 min-w-0 w-full">
            <h2 className="mb-2 text-base font-semibold text-gray-800">
              반려동물
            </h2>
            {isPetLoading ? (
              <div className="p-4 text-center text-gray-500 bg-white rounded-xl border border-gray-200 shadow-sm">
                반려동물 정보를 불러오는 중...
              </div>
            ) : petList.length === 0 ? (
              <div className="p-4 text-center text-gray-500 bg-white rounded-xl border border-gray-200 shadow-sm">
                등록된 반려동물이 없습니다.
              </div>
            ) : (
              <div className="flex flex-col gap-3 min-w-0 w-full">
                {petList.map((pet: PetDataType) => {
                  const isSelected = selectedPet?.petSeq === pet.petSeq;
                  return (
                    <button
                      key={pet.petSeq}
                      type="button"
                      className={`w-full max-w-full p-4 bg-white rounded-xl shadow-sm border cursor-pointer transition-colors text-left ${
                        isSelected
                          ? "border-orange-400"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setSelectedPet(pet)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex flex-1 gap-3 items-center min-w-0">
                          <div className="flex-shrink-0">
                            {pet.fileUrl ? (
                              <img
                                src={pet.fileUrl}
                                alt={pet.petName}
                                className="object-cover w-12 h-12 rounded-full"
                              />
                            ) : (
                              <div className="flex justify-center items-center w-12 h-12 bg-gray-200 rounded-full">
                                <svg
                                  className="w-6 h-6 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="mb-1 text-base font-semibold text-gray-800 truncate">
                              {pet.petName}
                            </h4>
                            <div className="flex gap-2 items-center text-sm text-gray-600 min-w-0">
                              {pet.petBreedName ? (
                                <span className="truncate">
                                  품종: {pet.petBreedName}
                                </span>
                              ) : null}
                              {pet.petWeight ? (
                                <>
                                  {pet.petBreedName ? (
                                    <span className="flex-shrink-0">·</span>
                                  ) : null}
                                  <span className="flex-shrink-0 whitespace-nowrap">
                                    체중: {pet.petWeight}kg
                                  </span>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex flex-shrink-0 justify-center items-center ml-2 w-5 h-5 bg-orange-400 rounded-full">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* 이용권 섹션 (반려동물 선택 후 표시) */}
          {selectedPet && (
            <div className="mt-6">
              {filteredSubscriptions.length === 0 ? (
                <div className="flex flex-col justify-center items-center py-20">
                  <p className="mb-4 text-gray-500">
                    {selectedPet.petName}에 사용할 수 있는 이용권이 없습니다.
                  </p>
                  <p className="text-sm text-gray-400">
                    이용권을 구매한 후 예약할 수 있습니다.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3 min-w-0 w-full">
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-base font-semibold text-gray-800">
                      보유한 이용권
                    </h2>
                    {/* 펼치기 버튼 (선택된 이용권이 있고 목록이 접혀있을 때만 표시) */}
                    {selectedSubscription && !isSubscriptionListExpanded && (
                      <button
                        type="button"
                        className="flex gap-1 items-center px-2 py-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                        onClick={handleSelectedSubscriptionClick}
                      >
                        <motion.svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          animate={{ rotate: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </motion.svg>
                      </button>
                    )}
                  </div>

                  {/* 선택된 이용권만 표시 (접힌 상태) */}
                  <AnimatePresence>
                    {selectedSubscription && !isSubscriptionListExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-x-hidden"
                      >
                        <div className="p-4 bg-white rounded-xl border border-orange-400 shadow-sm">
                          <div className="flex justify-between items-start min-w-0 w-full">
                            <div className="flex-1 min-w-0">
                              <div className="flex gap-2 items-start mb-1 min-w-0 flex-wrap">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-base font-semibold text-gray-800 break-words">
                                    {selectedSubscription.planName}
                                  </h3>
                                </div>
                                {selectedSubscription.ticketCount &&
                                  selectedSubscription.ticketCount > 1 && (
                                    <span className="flex-shrink-0 text-sm font-medium text-blue-500 whitespace-nowrap">
                                      ({selectedSubscription.ticketCount}장)
                                    </span>
                                  )}
                                {selectedSubscription.isCountBased &&
                                  !isHotel && (
                                    <span className="flex-shrink-0 text-sm font-medium whitespace-nowrap">
                                      <span className="text-gray-700">
                                        {(selectedSubscription.totalCount ??
                                          0) -
                                          (selectedSubscription.remainingCount ??
                                            0)}
                                        회 사용
                                      </span>
                                      <span
                                        className={
                                          selectedSubscription.remainingCount ===
                                          0
                                            ? "ml-1 text-gray-400"
                                            : "ml-1 text-orange-500"
                                        }
                                      >
                                        ({selectedSubscription.remainingCount}회
                                        남음)
                                      </span>
                                    </span>
                                  )}
                              </div>
                              <div className="flex gap-2 items-center text-sm text-gray-600 min-w-0 flex-wrap">
                                <span className="whitespace-nowrap">
                                  구매일: {selectedSubscription.purchaseDate}
                                </span>
                                {selectedSubscription.expiryDate && (
                                  <>
                                    <span className="flex-shrink-0">·</span>
                                    <span className="whitespace-nowrap">
                                      만료일: {selectedSubscription.expiryDate}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex justify-center items-center ml-2 w-5 h-5 bg-orange-400 rounded-full">
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* 전체 이용권 목록 (펼쳐진 상태) */}
                  <AnimatePresence>
                    {isSubscriptionListExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-3 min-w-0 w-full">
                          {filteredSubscriptions.map((subscription, index) => {
                            const isSelected =
                              selectedSubscription?.subscriptionSeq ===
                              subscription.subscriptionSeq;

                            return (
                              <motion.button
                                key={subscription.subscriptionSeq}
                                type="button"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{
                                  duration: 0.2,
                                  delay: index * 0.05,
                                }}
                                className={`w-full max-w-full p-4 bg-white rounded-xl shadow-sm border cursor-pointer transition-colors text-left ${
                                  isSelected
                                    ? "border-orange-400"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() =>
                                  handleSubscriptionSelect(subscription)
                                }
                              >
                                <div className="flex justify-between items-start min-w-0 w-full">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex gap-2 items-start mb-1 min-w-0 flex-wrap">
                                      <div className="flex-1 min-w-0">
                                        <h3 className="text-base font-semibold text-gray-800 break-words">
                                          {subscription.planName}
                                        </h3>
                                      </div>
                                      {subscription.ticketCount &&
                                        subscription.ticketCount > 1 && (
                                          <span className="flex-shrink-0 text-sm font-medium text-blue-500 whitespace-nowrap">
                                            ({subscription.ticketCount}장)
                                          </span>
                                        )}
                                      {subscription.isCountBased &&
                                        subscription.cdProductType !==
                                          "PRODUCT_TYPE_HOTEL" && (
                                          <span className="flex-shrink-0 text-sm font-medium whitespace-nowrap">
                                            <span className="text-gray-700">
                                              {(subscription.totalCount ?? 0) -
                                                (subscription.remainingCount ??
                                                  0)}
                                              회 사용
                                            </span>
                                            <span
                                              className={
                                                subscription.remainingCount ===
                                                0
                                                  ? "ml-1 text-gray-400"
                                                  : "ml-1 text-orange-500"
                                              }
                                            >
                                              ({subscription.remainingCount}회
                                              남음)
                                            </span>
                                          </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2 items-center text-sm text-gray-600 min-w-0 flex-wrap">
                                      <span className="whitespace-nowrap">
                                        구매일: {subscription.purchaseDate}
                                      </span>
                                      {subscription.expiryDate && (
                                        <>
                                          <span className="flex-shrink-0">
                                            ·
                                          </span>
                                          <span className="whitespace-nowrap">
                                            만료일: {subscription.expiryDate}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <div className="flex justify-center items-center ml-2 w-5 h-5 bg-orange-400 rounded-full">
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={3}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          )}

          {/* 날짜 선택 섹션 (반려견 + 이용권 모두 선택 시 표시) */}
          {selectedPet && selectedSubscription && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="mt-6">
                  {/* 섹션 헤더 — 예약하기는 하단 고정 버튼(상시 노출)으로 통일 */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex flex-col">
                      <h2 className="text-base font-semibold text-gray-800">
                        날짜 선택
                      </h2>
                      {!isHotel &&
                        maxSelectableDates !== null &&
                        maxSelectableDates > 0 && (
                          <p className="mt-1 text-xs text-gray-500">
                            최대 {maxSelectableDates}일까지 선택 가능합니다.
                          </p>
                        )}
                    </div>
                  </div>

                  {/* 달력 — 항상 표시 (아코디언 제거) */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <CalendarComponent
                        disabled={false}
                        onChange={handleDateChange}
                        selectedDays={selectedDates}
                        multiSelect={true}
                        disablePast={true}
                        maxDate={
                          selectedSubscription?.expiryDate
                            ? new Date(selectedSubscription.expiryDate)
                            : undefined
                        } // 선택한 이용권 만료일 이후 날짜 선택 불가 (만료일 당일 포함)
                        disabledDates={disabledDates}
                        handleSelectDate={handleCalendarMonthChange}
                        schedules={userCalendarData?.calendar ?? {}}
                      />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {/* 요일 선택 섹션 (날짜 선택 시 표시) */}
          {/* {selectedPet &&
            selectedDates.length > 0 &&
            reserveForms?.reserveForm?.dayOfWeeks &&
            reserveForms.reserveForm.dayOfWeeks.length > 0 && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-2">
                      <h2 className="text-base font-semibold text-gray-800">
                        요일 선택
                      </h2>
                      <button
                        type="button"
                        className="flex gap-1 items-center px-2 py-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                        onClick={handleDayOfWeekListToggle}
                      >
                        <motion.svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          animate={{
                            rotate: isDayOfWeekListExpanded ? 180 : 0,
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </motion.svg>
                      </button>
                    </div>

                    {!isDayOfWeekListExpanded && selectedDayOfWeek && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                      >
                        <span className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded">
                          {reserveForms.reserveForm.dayOfWeeks.find(
                            (d) => d.cdDayOfWeek === selectedDayOfWeek
                          )?.dayOfWeekName || selectedDayOfWeek}
                        </span>
                      </motion.div>
                    )}

                    {isDayOfWeekListExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                          <div className="grid grid-cols-4 gap-2">
                            {reserveForms.reserveForm.dayOfWeeks
                              .filter((day) => day.active !== false)
                              .map((day) => (
                                <button
                                  key={day.cdDayOfWeek}
                                  type="button"
                                  disabled={day.active === false}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    selectedDayOfWeek === day.cdDayOfWeek
                                      ? "bg-orange-400 text-white border-orange-400"
                                      : day.active === false
                                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                        : "bg-white text-gray-700 border-gray-300 hover:border-orange-300"
                                  }`}
                                  onClick={() => {
                                    if (selectedDayOfWeek === day.cdDayOfWeek) {
                                      setSelectedDayOfWeek(null);
                                    } else {
                                      setSelectedDayOfWeek(day.cdDayOfWeek);
                                    }
                                  }}
                                >
                                  {day.dayOfWeekName}
                                </button>
                              ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            )} */}

          {/* 호텔 전용: 시작 시간 선택 (시/분) */}
          {selectedPet && isHotel && (
            <div className="mt-6">
              <div className="mb-2">
                <h2 className="text-base font-semibold text-gray-800">
                  시작 시간
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  호텔 체크인 시작 시간을 선택해주세요.
                </p>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                {!firstSelectedDate ? (
                  <p className="text-sm text-center text-gray-400">
                    먼저 날짜를 선택해주세요.
                  </p>
                ) : hotelHourOptions.length === 0 && isHotelTimeFetching ? (
                  <div className="space-y-3 animate-pulse">
                    <div className="grid grid-cols-6 gap-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div
                          key={`h-skel-${i}`}
                          className="h-9 bg-gray-100 rounded-lg"
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div
                          key={`m-skel-${i}`}
                          className="h-9 bg-gray-100 rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                ) : hotelHourOptions.length === 0 ? (
                  <p className="text-sm text-center text-gray-400">
                    선택 가능한 시간이 없습니다.
                  </p>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs text-gray-500">
                        {formatDate(firstSelectedDate)} 기준
                      </span>
                      <span className="text-sm font-semibold text-orange-500">
                        {selectedHour && selectedMinute
                          ? `${selectedHour}:${selectedMinute}`
                          : "--:--"}
                      </span>
                    </div>
                    <div className="mb-2 text-xs font-medium text-gray-700">
                      시
                    </div>
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {hotelHourOptions.map((h) => {
                        const active = selectedHour === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setSelectedHour(h)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {h}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mb-2 text-xs font-medium text-gray-700">
                      분
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {hotelMinuteOptions.map((m) => {
                        const active = selectedMinute === m;
                        return (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setSelectedMinute(m)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* 데이케어 전용: 입소 시간(정시) + 이용시간 선택 */}
          {selectedPet && isDaycare && (
            <div className="mt-6">
              <div className="mb-2">
                <h2 className="text-base font-semibold text-gray-800">
                  입소 시간 / 이용시간
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  입소 시간(정시)과 이용시간을 선택해주세요. (1시간 = 이용권
                  1회)
                </p>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                {!firstSelectedDate ? (
                  <p className="text-sm text-center text-gray-400">
                    먼저 날짜를 선택해주세요.
                  </p>
                ) : hotelHourOptions.length === 0 && isHotelTimeFetching ? (
                  <div className="grid grid-cols-6 gap-2 animate-pulse">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={`dc-skel-${i}`}
                        className="h-9 bg-gray-100 rounded-lg"
                      />
                    ))}
                  </div>
                ) : hotelHourOptions.length === 0 ? (
                  <p className="text-sm text-center text-gray-400">
                    선택 가능한 시간이 없습니다.
                  </p>
                ) : daycareUseHoursOptions.length === 0 ? (
                  <p className="text-sm text-center text-red-500">
                    잔여 이용권이 부족하여 예약할 수 없습니다.
                  </p>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs text-gray-500">
                        {formatDate(firstSelectedDate)} 기준
                      </span>
                      <span className="text-sm font-semibold text-orange-500">
                        {selectedHour && selectedUseHours
                          ? `${selectedHour}:00 ~ ${String(
                              Number.parseInt(selectedHour, 10) +
                                selectedUseHours,
                            ).padStart(2, "0")}:00`
                          : "--:--"}
                      </span>
                    </div>
                    <div className="mb-2 text-xs font-medium text-gray-700">
                      입소 시간
                    </div>
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {hotelHourOptions.map((h) => {
                        const active = selectedHour === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setSelectedHour(h)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {h}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mb-2 text-xs font-medium text-gray-700">
                      이용시간
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {daycareUseHoursOptions.map((h) => {
                        const active = selectedUseHours === h;
                        return (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setSelectedUseHours(h)}
                            className={
                              "py-2 text-sm font-medium rounded-lg border transition-colors " +
                              (active
                                ? "bg-orange-400 text-white border-orange-400"
                                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300")
                            }
                          >
                            {h}시간
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* 시작 시간 선택 섹션 (날짜 선택 시 표시) */}
          {/* {selectedPet && selectedDates.length > 0 && ( */}
          {false && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="mt-3">
                  {/* 타이틀과 펼치기 버튼 */}
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-base font-semibold text-gray-800">
                      시작 시간
                    </h2>
                    <button
                      type="button"
                      className="flex gap-1 items-center px-2 py-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                      onClick={handleStartTimeListToggle}
                    >
                      <motion.svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        animate={{ rotate: isStartTimeListExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </motion.svg>
                    </button>
                  </div>

                  {/* 접혔을 때 선택된 시작 시간 표시 */}
                  {!isStartTimeListExpanded && startTime && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                    >
                      <span className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded">
                        {startTime}
                      </span>
                    </motion.div>
                  )}

                  {/* 펼쳐졌을 때 시간 선택 버튼 표시 */}
                  {isStartTimeListExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        {/* 오전 시간 */}
                        {amTimes.length > 0 && (
                          <div className="mb-4">
                            <div className="mb-2 text-sm font-medium text-gray-700">
                              오전
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {amTimes.map((time) => (
                                <button
                                  key={time}
                                  type="button"
                                  onClick={() => handleTimeSelect(time, true)}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    startTime === time
                                      ? "bg-orange-400 text-white border-orange-400"
                                      : "bg-white text-gray-700 border-gray-300 hover:border-orange-300"
                                  }`}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* 오후 시간 */}
                        {pmTimes.length > 0 && (
                          <div>
                            <div className="mb-2 text-sm font-medium text-gray-700">
                              오후
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {pmTimes.map((time) => (
                                <button
                                  key={time}
                                  type="button"
                                  onClick={() => handleTimeSelect(time, true)}
                                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                    startTime === time
                                      ? "bg-orange-400 text-white border-orange-400"
                                      : "bg-white text-gray-700 border-gray-300 hover:border-orange-300"
                                  }`}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {/* 종료 시간 선택 섹션 (시작 시간 선택 시 표시) */}
          {/* {selectedPet && selectedDates.length > 0 && startTime && ( */}
          {false && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="mt-3">
                  {/* 타이틀과 펼치기 버튼 */}
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-base font-semibold text-gray-800">
                      종료 시간
                    </h2>
                    <button
                      type="button"
                      className="flex gap-1 items-center px-2 py-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                      onClick={handleEndTimeListToggle}
                    >
                      <motion.svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        animate={{ rotate: isEndTimeListExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </motion.svg>
                    </button>
                  </div>

                  {/* 접혔을 때 선택된 종료 시간 표시 */}
                  {!isEndTimeListExpanded && endTime && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                    >
                      <span className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded">
                        {endTime}
                      </span>
                    </motion.div>
                  )}

                  {/* 펼쳐졌을 때 시간 선택 버튼 표시 */}
                  {isEndTimeListExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        {/* 오전 시간 */}
                        {amTimes.length > 0 && (
                          <div className="mb-4">
                            <div className="mb-2 text-sm font-medium text-gray-700">
                              오전
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {amTimes
                                .filter((time) => time > (startTime || "00:00"))
                                .map((time) => (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() =>
                                      handleTimeSelect(time, false)
                                    }
                                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                      endTime === time
                                        ? "bg-orange-400 text-white border-orange-400"
                                        : "bg-white text-gray-700 border-gray-300 hover:border-orange-300"
                                    }`}
                                  >
                                    {time}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* 오후 시간 */}
                        {pmTimes.length > 0 && (
                          <div>
                            <div className="mb-2 text-sm font-medium text-gray-700">
                              오후
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {pmTimes
                                .filter((time) => time > (startTime || "00:00"))
                                .map((time) => (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() =>
                                      handleTimeSelect(time, false)
                                    }
                                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                                      endTime === time
                                        ? "bg-orange-400 text-white border-orange-400"
                                        : "bg-white text-gray-700 border-gray-300 hover:border-orange-300"
                                    }`}
                                  >
                                    {time}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* 하단 고정 예약하기 버튼 — 조건(이용권+반려동물 선택) 충족 시 상시 노출 (사업자 예약 화면과 동일) */}
      {selectedSubscription && selectedPet && (
        <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-[480px] bottom-0 md:bottom-6 md:rounded-b-3xl z-40 px-4 pt-3 pb-[calc(0.75rem+var(--nav-bottom,0px)+env(safe-area-inset-bottom,0px))] bg-white border-t border-gray-200 shadow-lg">
          <Button
            className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 active:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleReservation()}
            disabled={
              isReserving ||
              selectedDates.length === 0 ||
              (isHotel && (!selectedHour || !selectedMinute)) ||
              (isDaycare && (!selectedHour || !selectedUseHours))
            }
          >
            {isReserving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                예약 처리 중...
              </span>
            ) : (
              "예약하기"
            )}
          </Button>
        </div>
      )}

      {/* 유치원 정원 초과 안내
          - 단일 날짜: "00월 00일은 모든 예약이 완료되었습니다." + [확인] (닫고 날짜 재선택)
          - 여러 날짜: 위 문구 + "해당 날짜를 제외하고 예약을 진행하시겠습니까?"
                      [예약 진행](초과 날짜 제외 진행) / [날짜 다시 선택하기](초과 날짜 자동 해제) */}
      <Dialog
        visible={!!capacityFull}
        onClose={() => setCapacityFull(null)}
        persist={false}
      >
        {capacityFull &&
          (() => {
            const fullSet = new Set(capacityFull.fullDates);
            const fullLabel = capacityFull.fullDates
              .map((d) => {
                const [, m, day] = d.split("-");
                return `${Number(m)}월 ${Number(day)}일`;
              })
              .join(", ");
            const remaining = selectedDates.filter(
              (d) => !fullSet.has(formatDate(d)),
            );
            // 여러 날짜를 골랐고 초과 날짜를 빼도 예약할 날짜가 남을 때만 "제외하고 진행" 선택지 제공
            const canProceedExcluding =
              capacityFull.selectedCount > 1 && remaining.length > 0;
            const deselectFullDates = () => {
              setSelectedDates(remaining);
              setCapacityFull(null);
            };
            return (
              <div className="flex flex-col gap-4">
                <p className="text-center text-sm text-gray-700 whitespace-pre-line">
                  {`${fullLabel}은 모든 예약이 완료되었습니다.`}
                  {canProceedExcluding
                    ? "\n해당 날짜를 제외하고 예약을 진행하시겠습니까?"
                    : ""}
                </p>
                {canProceedExcluding ? (
                  <div className="flex gap-3">
                    <Button
                      className="flex-1 py-2 text-base text-gray-600 bg-white rounded-full border border-gray-300"
                      onClick={deselectFullDates}
                    >
                      날짜 다시 선택하기
                    </Button>
                    <Button
                      className="flex-1 py-2 text-base text-white bg-orange-400 rounded-full"
                      onClick={() => {
                        setSelectedDates(remaining);
                        setCapacityFull(null);
                        handleReservation({
                          dates: remaining,
                          skipCapacityCheck: true,
                        });
                      }}
                    >
                      예약 진행
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="w-full py-2 text-base text-white bg-orange-400 rounded-full"
                    onClick={deselectFullDates}
                  >
                    확인
                  </Button>
                )}
              </div>
            );
          })()}
      </Dialog>

      {/* 날짜 제한 모달 */}
      <Dialog
        visible={isDateLimitModalOpen}
        onClose={() => {
          setIsDateLimitModalOpen(false);
          setDateLimitInfo(null);
        }}
        persist={false}
      >
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">
              날짜 선택 제한
            </h3>
            <p className="text-sm text-gray-600">
              선택 가능한 날짜는 최대 {dateLimitInfo?.maxCount}일입니다.
              <br />
              추가 이용권이 필요합니다.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              className="flex-1 py-2 text-base text-gray-600 bg-white rounded-full border border-gray-300"
              onClick={() => {
                setIsDateLimitModalOpen(false);
                setDateLimitInfo(null);
              }}
            >
              취소
            </Button>
            <Button
              className="flex-1 py-2 text-base text-white bg-orange-400 rounded-full"
              onClick={handleDateLimitModalConfirm}
            >
              이용권 구매하기
            </Button>
          </div>
        </div>
      </Dialog>

      {/* 계약 관련 다이얼로그 */}
      <Dialog
        visible={showContractDialog}
        onClose={() => {
          setShowContractDialog(false);
          // 펫이 이용권에 묶여있으므로 취소 시 이용권 선택을 해제
          setSelectedSubscription(null);
          setIsSubscriptionListExpanded(true);
        }}
        persist={false}
      >
        <div className="flex flex-col gap-4">
          <div className="text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">
              위탁계약 필요
            </h3>
            <p className="text-sm text-gray-600">
              해당 반려견의 위탁계약이 필요합니다.
              <br />
              계약을 진행하시겠습니까?
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              className="flex-1 py-2 text-base text-gray-600 bg-white rounded-full border border-gray-300"
              onClick={() => {
                setShowContractDialog(false);
                // 펫이 이용권에 묶여있으므로 취소 시 이용권 선택을 해제
                setSelectedSubscription(null);
                setIsSubscriptionListExpanded(true);
              }}
            >
              취소
            </Button>
            <Button
              className="flex-1 py-2 text-base text-white bg-orange-400 rounded-full"
              onClick={() => {
                if (!selectedPet?.petSeq || !companySeqNumber) return;
                // 서명 대상 계약 확보: 내게 온 대기 계약 우선, 없으면 자동발송 생성
                ensureContractMutation.mutate(
                  { companySeq: companySeqNumber, petSeq: selectedPet.petSeq },
                  {
                    onSuccess: (requestSeq) => {
                      setShowContractDialog(false);
                      if (requestSeq) {
                        setEnsuredRequestSeq(requestSeq);
                        setShowContractModal(true);
                      } else {
                        // 계약 불필요(회사에 활성 기본계약서 없음) → 그대로 예약 진행
                        refetchPetList();
                      }
                    },
                    onError: () => {
                      alert(
                        "계약서를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.",
                      );
                    },
                  },
                );
              }}
            >
              확인
            </Button>
          </div>
        </div>
      </Dialog>

      {/* 계약 모달 */}
      <ContractModal
        visible={showContractModal}
        onClose={() => {
          setShowContractModal(false);
          setEnsuredRequestSeq(null);
          // 펫이 이용권에 묶여있으므로 취소 시 이용권 선택을 해제
          setSelectedSubscription(null);
          setIsSubscriptionListExpanded(true);
        }}
        onConfirm={() => {
          // 서명 완료 후 계약 모달 닫기 및 펫 리스트 재조회
          setShowContractModal(false);
          setEnsuredRequestSeq(null);
          refetchPetList();
        }}
        selectedPet={
          selectedPet
            ? {
                petName: selectedPet.petName,
                fileUrl: selectedPet.fileUrl || null,
                petSeq: selectedPet.petSeq,
              }
            : undefined
        }
        companySeq={companySeqNumber}
        requestSeq={ensuredRequestSeq ?? undefined}
      />

      {/* 중복 예약 안내 다이얼로그 */}
      <Dialog
        visible={!!duplicateReserveInfo}
        onClose={() => setDuplicateReserveInfo(null)}
        persist={false}
      >
        <div className="p-6 text-center">
          <h3 className="mb-3 text-lg font-bold text-gray-800">
            {duplicateReserveInfo?.mode === "same"
              ? "이미 예약된 날짜입니다"
              : "다른 매장에 예약된 날짜입니다"}
          </h3>
          <p className="mb-6 text-sm text-gray-600 whitespace-pre-line">
            {duplicateReserveInfo?.mode === "same"
              ? "선택한 날짜에 같은 유형의 예약이 이미 등록되어 있어\n예약을 진행할 수 없습니다."
              : `현재 ${duplicateReserveInfo?.conflictCompanyName ?? ""}에 예약된 날짜입니다.\n예약을 진행하시겠습니까?`}
          </p>
          {duplicateReserveInfo?.mode === "same" ? (
            <div className="flex justify-center">
              <button
                className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
                onClick={() => setDuplicateReserveInfo(null)}
              >
                확인
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                className="flex-1 py-2 font-semibold text-gray-700 bg-white rounded-full border border-gray-300"
                onClick={() => setDuplicateReserveInfo(null)}
              >
                취소
              </button>
              <button
                className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                onClick={handleForceProceedReserve}
              >
                예약 진행
              </button>
            </div>
          )}
        </div>
      </Dialog>

      {/* 예약 완료 다이얼로그 */}
      <Dialog
        visible={showReservationCompleteDialog}
        onClose={() => {
          setShowReservationCompleteDialog(false);
          // 홈 페이지로 이동
          navigate({
            to: "/customer/layout/home",
          });
        }}
        persist={false}
      >
        <div className="p-6 text-center">
          <h3 className="mb-4 text-lg font-bold">예약 신청 완료</h3>
          {selectedDates.length > 0 && (
            <div className="p-3 mb-4 bg-gray-100 rounded-lg">
              <span className="font-bold text-black">
                {selectedDates.length === 1
                  ? formatDate(selectedDates[0]).replace(/-/g, ".")
                  : `${formatDate(selectedDates[0]).replace(/-/g, ".")} ~ ${formatDate(selectedDates[selectedDates.length - 1]).replace(/-/g, ".")}`}
              </span>
            </div>
          )}
          <p className="mb-6 text-sm text-gray-600">
            {getReservationCompleteMessage(selectedCdProductType)}
          </p>
          <div className="flex justify-center">
            <button
              className="px-8 py-3 font-semibold text-white bg-orange-400 rounded-lg"
              onClick={() => {
                setShowReservationCompleteDialog(false);
                // 홈 페이지로 이동
                navigate({
                  to: "/customer/layout/home",
                });
              }}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
